import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatBRL, STATUS_STYLES, PAYMENT_METHODS, PAYMENT_STATUS } from "@/lib/constants";
import { Plus, Wallet, TrendingUp, Download, FileText, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [months, setMonths] = useState(6);
  const [company, setCompany] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", document_id: "", amount: "", method: "PIX", status: "PAGO", date: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });

  const exportReport = async (fmt) => {
    try {
      const { data } = await api.get("/reports/financial", { params: { start: range.start, end: range.end, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio-financeiro.${fmt === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erro ao gerar relatório"); }
  };

  const load = () => Promise.all([api.get("/payments"), api.get("/clients"), api.get("/documents"), api.get("/company")]).then(([p, c, d, co]) => { setPayments(p.data); setClients(c.data); setDocs(d.data); setCompany(co.data); });
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/reports/monthly", { params: { n: months } }).then(({ data }) => setMonthly(data)); }, [months]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = payments.filter((p) => p.status === "PAGO" || p.status === "PARCIAL").reduce((s, p) => s + Number(p.amount || 0), 0);

  const save = async () => {
    if (!form.amount) { toast.error("Informe o valor"); return; }
    setBusy(true);
    try { await api.post("/payments", { ...form, amount: Number(form.amount) }); toast.success("Pagamento registrado!"); setOpen(false); setForm({ client_id: "", document_id: "", amount: "", method: "PIX", status: "PAGO", date: "", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="font-display font-extrabold text-2xl sm:text-3xl">Pagamentos</h1><p className="text-muted-foreground text-sm">Controle de recebimentos</p></div>
        <Button onClick={() => setOpen(true)} data-testid="new-payment-btn"><Plus className="h-4 w-4 mr-1" /> Registrar</Button>
      </div>

      <Card className="p-5 border-secondary/30">
        <div className="flex items-center justify-between">
          <div><p className="text-sm text-muted-foreground">Total recebido</p><p className="text-3xl font-display font-extrabold text-secondary">{formatBRL(total)}</p></div>
          <div className="h-12 w-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center"><TrendingUp className="h-6 w-6" /></div>
        </div>
      </Card>

      {company && Number(company.monthly_goal) > 0 && (
        <Card className="p-5" data-testid="goal-card">
          {(() => {
            const rec = monthly.length ? Number(monthly[monthly.length - 1].received || 0) : 0;
            const goal = Number(company.monthly_goal);
            const pct = Math.min(100, Math.round((rec / goal) * 100));
            return (
              <>
                <div className="flex items-center justify-between mb-2"><span className="font-semibold text-sm">Meta do mês</span><span className="text-sm text-muted-foreground">{formatBRL(rec)} / {formatBRL(goal)}</span></div>
                <div className="h-3 rounded-full bg-muted overflow-hidden"><div className="h-full bg-secondary rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                <p className="text-xs text-muted-foreground mt-1">{pct}% da meta atingida</p>
              </>
            );
          })()}
        </Card>
      )}

      <Card className="p-5" data-testid="finance-chart">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 text-primary font-semibold"><BarChart3 className="h-5 w-5" /> Recebido x Pendente</div>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="h-9 w-28" data-testid="chart-months"><SelectValue /></SelectTrigger>
            <SelectContent>{[3, 6, 12].map((n) => <SelectItem key={n} value={String(n)}>{n} meses</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={monthly} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="month" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} width={44} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
              <Tooltip formatter={(v) => formatBRL(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="received" name="Recebido" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pending" name="Pendente" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-5" data-testid="report-export-card">
        <div className="flex items-center gap-2 text-primary font-semibold mb-3"><Download className="h-5 w-5" /> Exportar relatório financeiro</div>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><Label className="text-xs">De</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="mt-1 h-10" data-testid="report-start" /></div>
          <div><Label className="text-xs">Até</Label><Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="mt-1 h-10" data-testid="report-end" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={() => exportReport("csv")} data-testid="export-csv-btn"><FileText className="h-4 w-4 mr-1" /> Excel (CSV)</Button>
          <Button variant="outline" onClick={() => exportReport("pdf")} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Recebido x pendente no período selecionado. Deixe as datas em branco para tudo.</p>
      </Card>

      {payments.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground"><Wallet className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>Nenhum pagamento registrado.</p></Card>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <Card key={p.id} className="p-4 flex items-center justify-between" data-testid={`payment-${p.id}`}>
              <div><p className="font-semibold">{p.client_name}</p><p className="text-xs text-muted-foreground">{p.method} • {p.date}</p></div>
              <div className="text-right"><p className="font-display font-bold text-secondary">{formatBRL(p.amount)}</p><Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[p.status] || ""}`}>{p.status}</Badge></div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar pagamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1 h-11" data-testid="payment-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor (R$)</Label><Input type="number" value={form.amount} onChange={set("amount")} className="mt-1 h-11" data-testid="payment-amount-input" /></div>
              <div><Label>Data</Label><DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="payment-date-input" /></div>
              <div><Label>Forma</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Observações</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-payment-btn">{busy ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
